/**
 * MY — Exempt Private Company Certificate (Section 260 & 261, M32016) chatflow.
 */
import type { ConversationMessage, PlaceholderAnalysis } from '@/features/AI-documents/types/docGen.types';
import type {
  ChatFlow,
  ChatFlowContext,
  ChatFlowEventResult,
} from '@/features/startTask/preset_task/shared/preset/chatflows/types';
import type { PreSetSelection } from '@/features/startTask/preset_task/shared/preset/types/preset.types';

export const exemptPrivateCompanyFlow: ChatFlow = {
  id: 'exempt-private-company-260',
  taskCodeMatch: ['M32016'],
  categoryMatch: [
    'exempt private company',
    'exempt private company certificate',
    'section 260',
    'section 261',
    'certificate of exempt private company',
    'statement by auditors',
  ],

  getInitialMessages(preSet: PreSetSelection): ConversationMessage[] {
    const docCount = preSet.documents.length;
    return [
      {
        role: 'ai',
        content:
          `Welcome to **Exempt Private Company Certificate (Section 260 & 261)**.\n\n` +
          `On the right, fill the live documents` +
          (docCount ? ` (${docCount} template${docCount > 1 ? 's' : ''} linked)` : '') +
          `:\n` +
          `1. Certificate of Exempt Private Company (Section 260)\n` +
          `2. Statement by Auditors (Section 261)\n\n` +
          `Use **✨** for person lookup, **📅** for dates and **📍** for addresses.\n\n` +
          `When ready, click **Generate** to create the task and documents.\n\n` +
          `First, please enter a **Task Title**.`,
        timestamp: Date.now(),
        stepId: 1,
        source: 'system',
      },
    ];
  },

  steps: [
    {
      id: 'input-title',
      aiMessage: 'Please enter a Task Title.',
      widget: 'text-input',
      widgetConfig: { targetVarKey: '_taskTitle', targetEntityIndex: 0 },
      awaitEvent: true,
    },
    {
      id: 'select-entity',
      aiMessage: 'Search and select the **entity** for this certificate. You can skip if not linked yet.',
      widget: 'entity-selector',
      widgetConfig: {
        targetVarKey: '_entity',
        targetEntityIndex: 0,
        allowSkip: true,
        analysis: {
          success: true, placeholder: '_entity', sourceResolver: 'ENTITY', officerRole: null,
          varKey: '_entity', varLabel: 'Entity', confidence: 1,
          message: 'Select Entity (Exempt Private Company).',
          relatedFields: [], options: [],
        } as PlaceholderAnalysis,
      },
      awaitEvent: true,
    },
    {
      id: 'confirm',
      aiMessage: 'Fill the documents on the right. Use **✨ / 📅 / 📍**, then **Generate**.',
      widget: null,
      nextStep: null,
    },
    { id: 'officer-role', aiMessage: 'Please select a role for the person you want to look up.', widget: 'officer-role-buttons', awaitEvent: true },
    { id: 'officer-list', aiMessage: 'Select a person from the list.', widget: 'officer-list', awaitEvent: true },
    { id: 'officer-detail', aiMessage: 'Person details loaded. The document has been pre-filled.', widget: 'officer-field-picker', awaitEvent: true },
    { id: 'address-input', aiMessage: 'Enter the address below.', widget: 'address-input', awaitEvent: true },
    { id: 'address-list', aiMessage: 'This person has multiple addresses. Pick the one to use.', widget: 'address-options', awaitEvent: true },
  ],

  handleEvent(eventName: string, payload: any, context: ChatFlowContext): ChatFlowEventResult {
    if (eventName === 'value-submit' && context.currentStepId === 'input-title') {
      const title = typeof payload === 'string' ? payload : payload?.value || payload;
      if (!title || !String(title).trim()) return {};
      return {
        messages: [{ role: 'user', content: String(title), timestamp: Date.now(), stepId: 1 }],
        formUpdates: { taskTitle: String(title).trim() },
        nextStepId: 'select-entity',
      };
    }

    if (eventName === 'entity-select' && context.currentStepId === 'select-entity') {
      const ent = payload || {};
      const entityCd = ent.entityCd || ent.icpCd || '';
      const entityName = ent.entityName || ent.companyName || 'Entity';
      if (!entityCd) return {};
      return {
        messages: [{ role: 'user', content: `Selected: **${entityName}**`, timestamp: Date.now(), stepId: 1 }],
        formUpdates: {
          entityCd, entityName, icpCd: entityCd,
          registrationNo: ent.registrationNo || '', entitySkipped: false, entityReselect: false,
        },
        nextStepId: 'confirm',
      };
    }

    if (eventName === 'entity-skip' && context.currentStepId === 'select-entity') {
      return {
        messages: [{ role: 'user', content: 'Skipped entity — continue without a linked company.', timestamp: Date.now(), stepId: 1 }],
        formUpdates: { entityCd: '', entityName: '', icpCd: '', registrationNo: '', tlinkData: null, entitySkipped: true },
        nextStepId: 'confirm',
      };
    }

    if (eventName === 'request-person-lookup') {
      const ctx = payload || {};
      const entityCd = context.entityCd || context.formData.entityCd || '';
      const defaultRole = ctx.defaultRole
        || (ctx.key === 'director_name' ? 'DIRECTOR'
          : ctx.key === 'secretary_name' ? 'SECRETARY'
            : ctx.key === 'audit_firm_name' ? 'AUDITOR'
              : null);
      if (!defaultRole) {
        return {
          messages: [{ role: 'user', content: `Look up a person for **${ctx.fieldGroup || 'this field'}**.`, timestamp: Date.now(), stepId: 1 }],
          nextStepId: 'officer-role',
          nextStepWidgetData: { entityCd },
        };
      }
      return {
        messages: [{ role: 'user', content: `Looking up **${defaultRole}**...`, timestamp: Date.now(), stepId: 1 }],
        nextStepId: 'officer-list',
        nextStepWidgetData: { entityCd, selectedRole: defaultRole },
      };
    }

    if (eventName === 'officer-role-select' && context.currentStepId === 'officer-role') {
      const role = payload;
      const entityCd = context.entityCd || context.formData.entityCd || '';
      return {
        messages: [{ role: 'user', content: `Role: **${role}**`, timestamp: Date.now(), stepId: 1 }],
        nextStepId: 'officer-list',
        nextStepWidgetData: { entityCd, selectedRole: role },
      };
    }

    if (eventName === 'officer-select' && context.currentStepId === 'officer-list') {
      return {
        messages: [{ role: 'user', content: 'Person selected.', timestamp: Date.now(), stepId: 1 }],
        nextStepId: 'officer-detail',
        nextStepWidgetData: payload,
      };
    }

    return {};
  },
};
